import type {Component, PanelLifecycleContext} from 'panel';

import type {Store} from 'common/report/stores/store';
import type {Section} from 'common/components/items-menu/types';

/**
 * A data controller defines the way a menu fetches, presents and reacts
 * to Store changes from the different Stores it might depend on, the overall goals of a
 * data controller are:
 * - it is consumed with `getSections()` and `data`
 * - must update the component(s) when data is available to render (is loaded, or loading for instance)
 * - must perform the right Store bookkeeping on the component's lifecycle
 *
 * - [PENDING] must initialize Store values from the component `selected` attr (custom props, cohorts, temp mappings)
 */
export abstract class BaseDataController implements PanelLifecycleContext {
  protected connectedComponents: Set<Component<any>>;
  protected loading = false;
  protected onDataUpdated: () => void;
  protected _onStoreCallback = () => this.onStoreCallback();
  public data: Map<string, Obj> = null;

  constructor() {
    this.connectedComponents = new Set();
    this.providerStores.forEach((store) => store.subscribe(this._onStoreCallback));
  }

  protected onStoreCallback() {
    throw new Error(`not implemented`);
  }

  public get providerStores(): Array<Store<unknown>> {
    return [];
  }

  public fetchData(..._params) {
    throw new Error(`not implemented`);
  }

  public getSections(..._params): Section[] {
    throw new Error(`not implemented`);
  }

  public updateComponents() {
    for (const component of this.connectedComponents) {
      component.update();
    }
  }

  public bindToComponent(component: Component<any>) {
    component.onConnected(() => this.fetchData());
    component.onDisconnected(() => this.providerStores.forEach((store) => store.unsubscribe(this._onStoreCallback)));
    this.connectedComponents.add(component);
  }

  public unbindFromComponent(component: Component<any>) {
    this.connectedComponents.delete(component);
  }
}
